<template>
  <DashboardCard>
    <template #title>
      <WdsStack gap-sm class="tw-items-center tw-justify-between tw-w-full">
        <span>{{ title }}</span>
        <WdsButtonGhost :href="laravelNamedRoute(seeOlderRoute)" secondary class="tw-items-center">
          {{ $t('See older') }}
          <WdsIcon :size="16" name="chevron-right" />
        </WdsButtonGhost>
      </WdsStack>
    </template>

    <WdsStack direction-column class="tw-pb-4">
      <!-- Show Spinner While Loading -->
      <div v-if="loading">
        <LoadingList :count="1" />
      </div>

      <!-- Show No Results Message -->
      <WdsStack
        v-else-if="contracts.length === 0"
        align-center
        direction-column
        justify-center
        gap-md
      >
        <WdsText secondary class="tw-text-center">
          {{ emptyStateText }}
        </WdsText>
        <WdsButton v-if="emptyStateAction" secondary :href="emptyStateAction.href">
          {{ emptyStateAction.label }}
        </WdsButton>
      </WdsStack>

      <!-- Contracts List -->
      <BaseList v-else class="tw-max-h-[377px] tw-overflow-y-auto">
        <div v-for="hire in contracts" :key="hire.id">
          <!-- Contract changes badge -->
          <WdsStack
            v-if="hire.pendingContractChanges"
            class="tw-bg-orange-50 tw-px-2 tw-py-1 tw--mb-2 tw-rounded-md tw-relative tw-z-20"
          >
            <WdsText>
              <div class="tw-text-orange-600 tw-font-semibold">
                {{ $t('New contract changes!') }}
              </div>
            </WdsText>
          </WdsStack>

          <BaseListRow surface="grey" padding="none" hoverable class="tw-flex tw-flex-col">
            <WdsStack justify-space-between align-start gap-md class="tw-w-full tw-p-4">
              <!-- Left side: Company info and contract details -->
              <WdsStack direction-column gap-xs class="tw-flex-1">
                <!-- Company name and status badge -->
                <WdsStack align-center gap-sm>
                  <WdsText class="tw-font-medium">{{ hire.company.name }}</WdsText>
                  <HireActiveStatusLabel :status="hire.activeStatus" sm />
                </WdsStack>

                <!-- Job title -->
                <a :href="`/hires/${hire.id}/overview`">
                  <WdsText>
                    <div class="tw-text-indigo-500 tw-cursor-pointer tw-line-clamp-1">
                      {{ hire.job.name }}
                    </div>
                  </WdsText>
                </a>

                <!-- Contract dates -->
                <WdsText small secondary>
                  <div class="tw-font-medium">
                    {{ formatDateRange(hire.startDate, hire.endDate) }}
                  </div>
                </WdsText>
              </WdsStack>

              <!-- Right side: Action button -->
              <WdsStack class="tw-h-full tw-m-auto" justify-center align-center>
                <WdsButton
                  v-if="
                    hire.hasDraftContract && !hire.hasSignedScheduledChange && hire.supplierHire
                  "
                  :href="`/hires/${hire.id}/overview`"
                >
                  {{ $t('View hire') }}
                </WdsButton>
                <WdsButton
                  v-else-if="
                    hire.hasDraftContract &&
                    !hire.hasSignedScheduledChange &&
                    viewerConfirmsContract(hire)
                  "
                  primary
                  :href="`/hires/${hire.id}/overview?acceptcontract=1`"
                >
                  {{ $t('Review') }}
                </WdsButton>
                <WdsButtonGhost
                  v-else
                  :href="`/hires/${hire.id}/overview`"
                  class="tw-items-center tw-gap-sm"
                >
                  {{ $t('View') }}
                </WdsButtonGhost>
              </WdsStack>
            </WdsStack>

            <!-- Action buttons row -->
            <WdsStack justify-space-between class="tw-p-2 tw-border-t tw-border-gray-100">
              <WdsStack gap-md>
                <!-- Message button -->
                <WdsButtonGhost
                  :href="laravelNamedRoute('messages.show', { id: hire.conversation?.id })"
                  class="tw-items-center tw-text-sm"
                  :disabled="!hire.conversation"
                >
                  <div class="tw-relative">
                    <WdsIcon :size="16" name="chat" class="tw-text-gray-700" />
                    <div
                      v-if="hire.conversation?.isUnread"
                      class="tw-absolute tw--top-0.5 tw--right-0.5 tw-bg-red-600 tw-rounded-full tw-size-2"
                    ></div>
                  </div>
                  <span class="tw-ml-1">{{ $t('Message') }}</span>
                </WdsButtonGhost>

                <!-- Request payment button -->
                <div
                  v-if="canRequestPayment(hire)"
                  :class="{
                    'tw-opacity-50 tw-cursor-not-allowed tw-pointer-events-none':
                      hire.activeStatus === HireActiveStatus.Offered ||
                      hire.activeStatus === HireActiveStatus.Ready,
                  }"
                >
                  <WdsButtonGhost
                    :href="laravelNamedRoute('bills.create', { hire: hire.id })"
                    class="tw-items-center tw-text-sm"
                  >
                    <WdsIcon :size="16" name="receipt" class="tw-text-gray-700" />
                    <span class="tw-ml-1">{{ $t('Request payment') }}</span>
                  </WdsButtonGhost>
                </div>
              </WdsStack>

              <!-- More actions menu -->
              <WdsMenu placement="bottom-end" z-index="50" manual-trigger>
                <template #trigger="{ toggle }">
                  <div class="tw-pt-0.5">
                    <WdsButtonGhost class="tw-text-sm" @click="toggle">
                      <WdsIcon name="overflow-menu-vertical" :size="16" class="tw-text-gray-600" />
                    </WdsButtonGhost>
                  </div>
                </template>
                <WdsMenuOption :href="laravelNamedRoute('jobs.show', { job: hire.job.id })">
                  <WdsIcon :size="16" name="portfolio" />
                  {{ $t('View job') }}
                </WdsMenuOption>
                <WdsMenuOption
                  :href="laravelNamedRoute('yjob.contract', { hire: hire?.id })"
                  :disabled="hire.activeStatus === HireActiveStatus.Offered"
                >
                  <WdsIcon :size="16" name="download" />
                  {{ $t('Download contract') }}
                </WdsMenuOption>
              </WdsMenu>
            </WdsStack>
          </BaseListRow>
        </div>
      </BaseList>
    </WdsStack>
  </DashboardCard>
</template>

<script setup lang="ts">
import {
  WdsStack,
  WdsText,
  WdsButton,
  WdsButtonGhost,
  WdsIcon,
  WdsMenu,
  WdsMenuOption,
} from '@worksome/wds-components'
import moment from 'moment'
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import BaseListRow from '@/components/BaseListRow.vue'
import BaseList from '@/components/BaseList.vue'
import { graphql, useFragment } from '@/gql'
import DashboardCard from '@/components/Dashboard/DashboardCard.vue'
import HireActiveStatusLabel from '@/components/Common/HireActiveStatusLabel.vue'
import { useTranslation } from '@/composables/translation'
import LoadingList from '@/components/Dashboard/Widgets/Utils/LoadingList.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import { HireActiveStatus } from '@/gql/graphql'
import { useViewerConfirmsContract } from '@/components/Hire/composables/useViewerSignsContract'

interface EmptyStateAction {
  href: string
  label: string
}

const props = withDefaults(
  defineProps<{
    title?: string
    seeOlderRoute?: string
    emptyStateText?: string
    emptyStateAction?: EmptyStateAction | null
    showRequestPayment?: boolean | 'managed-only'
  }>(),
  {
    title: undefined,
    seeOlderRoute: 'frontend.contracts.my-contracts',
    emptyStateText: undefined,
    emptyStateAction: undefined,
    showRequestPayment: true,
  },
)

const { $t } = useTranslation()

const title = computed(() => props.title ?? $t('My contracts'))
const emptyStateText = computed(
  () => props.emptyStateText ?? $t("You don't have any active contracts or offers"),
)
const emptyStateAction = computed<EmptyStateAction | null>(() => {
  if (props.emptyStateAction !== undefined) {
    return props.emptyStateAction
  }
  return {
    href: laravelNamedRoute('frontend.jobs.index'),
    label: $t('Find your new job on the marketplace!'),
  }
})

// Shared fragment for hire data
const MyContractHireFragment = graphql(`
  fragment MyContractHireFields on Hire {
    id
    number
    activeStatus
    startDate
    endDate
    hasDraftContract
    pendingContractChanges
    hasSignedScheduledChange
    createdAt
    company {
      id
      name
    }
    job {
      id
      name
    }
    conversation {
      id
      url
      isClosed
      isUnread
    }
    activeContract {
      id
      status
      startDate
      endDate
    }
    recruiterManagesWorkers
    viewerIsSupplier
    supplierSignatureWaived
    supplierHire {
      id
    }
  }
`)

// Query for active hires (offered, ready, active)
const { result: activeResult, loading: activeLoading } = useQuery(
  graphql(`
    query MyActiveContracts {
      hires(
        first: 30
        orderBy: [{ field: CREATED_AT, order: DESC }]
        activeStatus: [OFFERED, READY, ACTIVE]
      ) {
        data {
          ...MyContractHireFields
        }
      }
    }
  `),
  {},
  {
    clientId: 'publicApi',
  },
)

// Calculate date for filtering
const oneMonthAgo = moment().subtract(1, 'month').format('YYYY-MM-DD')

// Query for recently ended hires from the past month
const { result: endedResult, loading: endedLoading } = useQuery(
  graphql(`
    query MyRecentlyEndedContracts($endsAfter: Date!) {
      hires(
        first: 30
        orderBy: [{ field: CREATED_AT, order: DESC }]
        activeStatus: [ENDED, TERMINATED]
        endsAfter: $endsAfter
      ) {
        data {
          ...MyContractHireFields
        }
      }
    }
  `),
  {
    endsAfter: oneMonthAgo,
  },
  {
    clientId: 'publicApi',
  },
)

const loading = computed(() => activeLoading.value || endedLoading.value)

const contracts = computed(() => {
  const activeHires =
    activeResult.value?.hires?.data?.map((hire) => useFragment(MyContractHireFragment, hire)) || []
  const endedHires =
    endedResult.value?.hires?.data?.map((hire) => useFragment(MyContractHireFragment, hire)) || []

  const allHires = [...activeHires, ...endedHires]

  return allHires.sort((a, b) => {
    // Priority 1: Pending contract changes OR offered status (highest priority)
    const aIsHighPriority = a.pendingContractChanges || a.activeStatus === HireActiveStatus.Offered
    const bIsHighPriority = b.pendingContractChanges || b.activeStatus === HireActiveStatus.Offered

    if (aIsHighPriority && !bIsHighPriority) return -1
    if (!aIsHighPriority && bIsHighPriority) return 1

    // Priority 2: Ready and Active status (middle priority)
    const aIsMiddlePriority =
      a.activeStatus === HireActiveStatus.Ready || a.activeStatus === HireActiveStatus.Active
    const bIsMiddlePriority =
      b.activeStatus === HireActiveStatus.Ready || b.activeStatus === HireActiveStatus.Active

    if (aIsMiddlePriority && !bIsMiddlePriority && !bIsHighPriority) return -1
    if (!aIsMiddlePriority && bIsMiddlePriority && !aIsHighPriority) return 1

    // Priority 3: Ended and Terminated status (lowest priority) - handled by default

    // Within the same priority group, sort by created date (newest first)
    const dateA = moment(a.createdAt)
    const dateB = moment(b.createdAt)
    return dateB.diff(dateA)
  })
})

const canRequestPayment = (hire: { recruiterManagesWorkers?: boolean | null }) => {
  if (props.showRequestPayment === true) return true
  if (props.showRequestPayment === false) return false
  return hire.recruiterManagesWorkers === true
}

// Confirms, not signs: the CTA must stay for a supplier whose signature is
// waived (JIRA-17526) — they still accept the contract, just without signing.
const viewerConfirmsContract = useViewerConfirmsContract()

const formatDateRange = (startDate: string | null, endDate: string | null): string => {
  if (!startDate && !endDate) return ''

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null
    return moment(dateStr).format('DD MMM YYYY')
  }

  const start = formatDate(startDate)
  const end = formatDate(endDate)

  if (start && end) {
    return `${start} - ${end}`
  }

  if (start) {
    return `${start} - ${$t('No end date')}`
  }

  return ''
}
</script>
